import { Component } from 'react';
import { FaBrain, FaCloud, FaCode, FaExternalLinkAlt } from 'react-icons/fa';
import { VscAzure, VscCopilot } from 'react-icons/vsc';
import { careerEntries } from './career-data';
import { CountryFlag } from '../country-flag/country-flag';
import { getCareerYears } from '../../utils/career-duration';
import './career-cloud-ai-stack.css';

const getCurrentEmployer = () => {
    const currentEmployer = careerEntries.find((entry) => entry.layer === 'current');

    if (!currentEmployer) {
        throw new Error('Career data must include a current employer.');
    }

    return currentEmployer;
};

const microsoft = getCurrentEmployer();
const currentRole = microsoft.roles[0];
const engineeringTenures = careerEntries.filter((entry) => entry.layer === 'engineering');
const foundationTenures = careerEntries.filter((entry) => entry.layer === 'foundation');
const roleCount = careerEntries.reduce((total, entry) => total + entry.roles.length, 0);

export class CareerCloudAiStack extends Component {
    render() {
        return (
            <section id="career" className="career-cloud-stack" aria-labelledby="career-cloud-stack-heading">
                <header className="career-cloud-stack-header">
                    <div>
                        <p>CAREER://PRODUCTION/ARCHITECTURE</p>
                        <h2 id="career-cloud-stack-heading">Cloud AI Career Stack</h2>
                    </div>
                    <div className="career-cloud-stack-status" role="group" aria-label="Career summary">
                        <span><i aria-hidden="true" /> {getCareerYears()} years of experience</span>
                        <span>{roleCount} role deployments</span>
                    </div>
                </header>

                <div className="career-cloud-stack-workspace">
                    <div className="career-cloud-stack-command" aria-hidden="true">
                        <span>career-architecture.ts</span>
                        <code>deploy({'{'} target: 'cloud-ai', region: 'saudi-arabia' {'}'})</code>
                    </div>

                    <svg className="career-cloud-stack-links" viewBox="0 0 1000 920" preserveAspectRatio="none" aria-hidden="true">
                        <defs>
                            <linearGradient id="career-cloud-link-gradient" x1="0" y1="1" x2="0" y2="0">
                                <stop offset="0" stopColor="#2e4057" />
                                <stop offset="0.52" stopColor="#39727b" />
                                <stop offset="1" stopColor="#80381a" />
                            </linearGradient>
                        </defs>
                        <path className="career-cloud-stack-link-bed" d="M 500 860 L 500 690 M 500 690 C 500 630 275 630 275 550 M 500 690 C 500 630 725 630 725 550 M 275 440 C 275 375 500 375 500 315 M 725 440 C 725 375 500 375 500 315 M 500 205 L 500 115" />
                        <path className="career-cloud-stack-link-signal" d="M 500 860 L 500 690 M 500 690 C 500 630 275 630 275 550 M 500 690 C 500 630 725 630 725 550 M 275 440 C 275 375 500 375 500 315 M 725 440 C 725 375 500 375 500 315 M 500 205 L 500 115" />
                        <circle className="career-cloud-stack-packet career-cloud-stack-packet--one" r="7">
                            <animateMotion dur="5.8s" repeatCount="indefinite" path="M 500 860 L 500 690 C 500 630 275 630 275 550 C 275 375 500 375 500 315 L 500 115" />
                        </circle>
                        <circle className="career-cloud-stack-packet career-cloud-stack-packet--two" r="6">
                            <animateMotion begin="1.9s" dur="5.8s" repeatCount="indefinite" path="M 500 860 L 500 690 C 500 630 725 630 725 550 C 725 375 500 375 500 315 L 500 115" />
                        </circle>
                        <circle className="career-cloud-stack-packet career-cloud-stack-packet--three" r="5">
                            <animateMotion begin="3.8s" dur="5.8s" repeatCount="indefinite" path="M 500 860 L 500 690 C 500 630 275 630 275 550 C 275 375 500 375 500 315 L 500 115" />
                        </circle>
                    </svg>

                    <div className="career-cloud-stack-layer career-cloud-stack-layer--ai">
                        <span className="career-cloud-stack-layer-label"><FaBrain aria-hidden="true" /> AI application layer</span>
                        <div className="career-cloud-stack-current">
                            <div className="career-cloud-stack-current-icon" aria-hidden="true">
                                <VscCopilot />
                            </div>
                            <div>
                                <div className="career-cloud-stack-current-meta">
                                    <span>ACTIVE OUTPUT / {currentRole.startYear}</span>
                                    <span className="career-cloud-stack-current-location">
                                        <CountryFlag country={currentRole.country ?? microsoft.country} />
                                        {currentRole.country ?? microsoft.country}
                                    </span>
                                </div>
                                <h3>{currentRole.title}</h3>
                                {currentRole.summary && <p>{currentRole.summary}</p>}
                            </div>
                            <strong>RUNNING</strong>
                        </div>
                    </div>

                    <div className="career-cloud-stack-layer career-cloud-stack-layer--cloud">
                        <span className="career-cloud-stack-layer-label"><FaCloud aria-hidden="true" /> Cloud architecture layer</span>
                        <div className="career-cloud-stack-service career-cloud-stack-service--azure">
                            <VscAzure aria-hidden="true" />
                            <div>
                                <span>AZURE / MICROSOFT</span>
                                <h3>
                                    <a href={microsoft.website} target="_blank" rel="noreferrer">
                                        {microsoft.company}<FaExternalLinkAlt aria-hidden="true" />
                                    </a>
                                </h3>
                                <p><CountryFlag country={microsoft.country} /> {microsoft.country}</p>
                            </div>
                        </div>
                        <ol aria-label={`Role history at ${microsoft.company}`}>
                            {microsoft.roles.slice(1).map((role) => (
                                <li key={`${role.title}-${role.startYear}`}>
                                    <time dateTime={role.startYear}>{role.startYear}</time>
                                    <span className="career-cloud-stack-role">
                                        <span>{role.title}</span>
                                        <small>
                                            <CountryFlag country={role.country ?? microsoft.country} />
                                            {role.country ?? microsoft.country}
                                        </small>
                                    </span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div className="career-cloud-stack-layer career-cloud-stack-layer--services">
                        <span className="career-cloud-stack-layer-label"><FaCode aria-hidden="true" /> Engineering services layer</span>
                        <ol aria-label="Engineering company tenures">
                            {engineeringTenures.map((entry) => (
                                <li key={entry.tenureId}>
                                    <img src={entry.logo} alt="" height="46" width="46" loading="lazy" />
                                    <div>
                                        <h3>
                                            <a href={entry.website} target="_blank" rel="noreferrer">
                                                {entry.company}<FaExternalLinkAlt aria-hidden="true" />
                                            </a>
                                        </h3>
                                        <p><CountryFlag country={entry.country} /> {entry.country}</p>
                                        <ul aria-label={`Roles at ${entry.company}`}>
                                            {entry.roles.map((role) => (
                                                <li key={`${role.title}-${role.startYear}`}>{role.title}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <span>{entry.roles.map((role) => role.startYear).join(' / ')}</span>
                                </li>
                            ))}
                        </ol>
                    </div>

                    <div className="career-cloud-stack-layer career-cloud-stack-layer--foundation">
                        <span className="career-cloud-stack-layer-label"><FaCode aria-hidden="true" /> Code foundation</span>
                        <ol aria-label="Career foundation roles">
                            {foundationTenures.map((entry) => (
                                <li key={entry.tenureId}>
                                    <code>&lt;{entry.company.replace(/\s+/g, '-').toLowerCase()} /&gt;</code>
                                    <p>{entry.roles[0].title}</p>
                                    <small>
                                        <CountryFlag country={entry.country} /> {entry.country}
                                    </small>
                                    <time dateTime={entry.roles[0].startYear}>{entry.roles[0].startYear}</time>
                                </li>
                            ))}
                        </ol>
                    </div>
                </div>
            </section>
        );
    }
}